import moment from "moment";

// Google hands back a wall clock carrying its own offset:
// "2026-08-21T20:00:00-07:00". The day a meeting belongs to is the date written
// in THAT offset — not the day the same instant happens to land on in the
// viewer's timezone.
//
// This matters because useEvents queries the left rail by string-comparing
// `start.dateTime` against "<YYYY-MM-DD>T00:00:00.000Z", so it is really
// matching on the event's own written date. Handing it a day derived from the
// viewer's clock is what made an evening meeting in California open the next
// day for a viewer in Pakistan.
const DAY_FORMAT = "YYYY-MM-DD";

// A trailing "+05:30" or "-0700" is the meeting's own wall clock. A trailing
// "Z" is not: toISOString() normalises every instant to UTC, so a Z value says
// nothing about the day the meeting was written in.
const WRITTEN_OFFSET = /[+-]\d{2}:?\d{2}$/;

const parseMeetingValue = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (typeof value === "string") {
    // parseZone keeps the offset the string carries instead of converting it
    // to the viewer's clock.
    const written = moment.parseZone(value, moment.ISO_8601);
    if (written.isValid()) return written;

    // A formatted or RFC-style date carries no offset, so the viewer's own
    // clock is the best available answer.
    const loose = moment(new Date(value));
    return loose.isValid() ? loose : null;
  }

  // A Date or a timestamp carries no written offset either.
  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
};

/** The calendar day a meeting belongs to, as "YYYY-MM-DD". */
export const getMeetingDayString = (value) => {
  const parsed = parseMeetingValue(value);
  return parsed ? parsed.format(DAY_FORMAT) : null;
};

/** Midnight of that day in the viewer's timezone, for the date pickers. */
export const localDateFromDayString = (value) => {
  if (typeof value !== "string" || !value) return null;

  // Strict, so "2026-13-45" is rejected rather than rolled over into 2027.
  const day = moment(value, DAY_FORMAT, true);
  if (day.isValid()) return day.toDate();

  // A full timestamp only reaches here from links written before the day
  // string existed. Those used toISOString(), so a Z value is an instant with
  // no written offset and has to be read back on the viewer's clock; an
  // explicit offset is the meeting's own wall clock and is kept as written.
  const parsed = WRITTEN_OFFSET.test(value)
    ? moment.parseZone(value, moment.ISO_8601)
    : moment(value, moment.ISO_8601);
  if (!parsed.isValid()) return null;

  return moment(parsed.format(DAY_FORMAT), DAY_FORMAT).toDate();
};
